import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, ArrowLeft, Zap, Cpu, Palette, Target } from 'lucide-react';

// Hero Section with Video Background
const Hero = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  return (
    <section className="min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-elate-black/70 z-10" />
        
        {/* Fallback image shown before video loads */}
        <img
          src="/images/elate-home-fallback.png"
          alt="Elate Global"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
        />
        
        <video
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className={`w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          poster="/images/elate-home-fallback.png"
        >
          <source src="https://streamable.com/l/3hrcq5/mp4.mp4" type="video/mp4" />
        </video>
      </div>

      <div className="container-custom relative z-20 pt-32 pb-20">
        <div className="max-w-4xl">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            AI-First Creative Studio
          </motion.p>

          <motion.h1
            className="text-display text-white mb-4"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Empowering
            <br />
            <span className="text-elate-yellow block mt-3 md:mt-2">Brands</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-300 max-w-2xl mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            We saw the change coming. Now we're building brands beyond web presence—developing in-house AI systems that future-proof businesses and drive growth in the new era.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta">
              Start a Project
              <ArrowUpRight size={20} />
            </Link>
            <Link to="/work" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-work-link">
              View Our Work
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>

        {/* Arrow scroll indicator on right */}
        <motion.div
          className="absolute bottom-12 right-8 md:right-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <motion.div
            className="flex flex-col items-center gap-3 cursor-pointer"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-xs uppercase tracking-widest text-white/50 writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>
              Scroll
            </span>
            <svg 
              width="24" 
              height="40" 
              viewBox="0 0 24 40" 
              fill="none" 
              className="text-elate-yellow"
            >
              <path 
                d="M12 0L12 36M12 36L2 26M12 36L22 26" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Featured Work Section - NO AUTOPLAY, Manual navigation only
const FeaturedWork = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filters = ['all', 'city', 'sport', 'music', 'entertainment'];

  const allProjects = [
    // City - Flagship clients FIRST
    {
      title: 'Brandsmiths',
      subtitle: 'Award-Winning Law Firm',
      category: 'city',
      image: '/images/clients/Brandsmiths Background - City.jpg',
      logo: '/images/clients/Brandsmiths Logo.png',
      logoSize: 'xlarge',
      link: 'https://brandsmiths.co.uk/',
      gradient: 'from-[#E91E63]/40 via-transparent to-transparent',
      gradientDirection: 'bg-gradient-to-t',
    },
    {
      title: 'VenCap',
      subtitle: 'Investment Management',
      category: 'city',
      image: '/images/clients/Vencap background - City.png',
      logo: '/images/clients/VenCap Logo.png',
    },
    {
      title: 'NicheCom',
      subtitle: 'Visual Property Marketing',
      category: 'city',
      image: '/images/clients/NicheCom.png',
      logo: '/images/clients/Nichecom-Logo-White.png',
      gradient: 'from-[#4CAF50]/60 via-transparent to-transparent',
      gradientDirection: 'bg-gradient-to-b',
    },
    {
      title: 'Hagen Wolf',
      subtitle: 'Award-Winning Law Firm',
      category: 'city',
      image: '/images/clients/HAgen Wolf Background - City.png',
      logo: '/images/clients/HAgen Wolf Logo.png',
      link: 'https://hagenwolf.co.uk/',
    },
    {
      title: 'KKV',
      subtitle: 'Investment Fund',
      category: 'city',
      image: '/images/clients/obgImage-1551195.jpg',
      logo: '/images/clients/KKV Logo.png',
    },
    {
      title: 'LTS Group',
      subtitle: 'SEND Recruitment',
      category: 'city',
      image: '/images/clients/LTS Background - City.jpg',
      logo: '/images/clients/LTS Group Logo.png',
      logoSize: 'large',
    },
    // Sport - New order: Callum, Lawrence, Joe, Johnny
    {
      title: 'Callum Ilott',
      subtitle: 'F1 Racing Driver',
      category: 'sport',
      image: '/images/clients/Callum Ilott - Sport.jpg',
      link: 'https://callumilott.com/',
    },
    {
      title: 'Lawrence Okolie',
      subtitle: 'World Champion Boxer',
      category: 'sport',
      image: '/images/clients/laurence okolie world title.jpg',
    },
    {
      title: 'Joe Joyce',
      subtitle: 'Heavyweight Boxer',
      category: 'sport',
      image: '/images/clients/Joe Joyce - sport.jpg',
      link: 'https://joejoyceboxing.com/',
    },
    {
      title: 'Johnny Fisher',
      subtitle: 'Heavyweight Boxer',
      category: 'sport',
      image: '/images/clients/Johnny fisher - Sport.jpg',
    },
    // Music - Stiles & Drewe first, Kerry Ellis, NO Aled Jones
    {
      title: 'Stiles & Drewe',
      subtitle: 'Theatre Composers',
      category: 'music',
      image: '/images/clients/stilesdrewe.jpg',
    },
    {
      title: 'Kerry Ellis',
      subtitle: 'West End Star',
      category: 'music',
      image: '/images/clients/Kerry Ellis.jpg',
    },
    {
      title: 'Mark Kingswood',
      subtitle: 'Music Artist',
      category: 'music',
      image: '/images/clients/Mark Kingswood - Music.jpg',
    },
    // Entertainment
    {
      title: 'Teletubbies',
      subtitle: '1st Ever Theatre Tour',
      category: 'entertainment',
      image: '/images/clients/Teletubbies Entertainment.jpg',
    },
    {
      title: 'Tom Gates',
      subtitle: 'National Tour',
      category: 'entertainment',
      image: '/images/clients/Brilliant World Of Tom Gates - Entertainment.jpg',
    },
    {
      title: 'Fireman Sam',
      subtitle: 'National Tour',
      category: 'entertainment',
      image: '/images/clients/Fireman Sam - entertainment.jpg',
    },
  ];

  const filteredProjects = activeFilter === 'all' 
    ? allProjects 
    : allProjects.filter(p => p.category === activeFilter);

  // Reset index when filter changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeFilter]);

  const handlePrev = () => {
    // Move by 3 on desktop, 1 on mobile
    const step = isMobile ? 1 : 3;
    setCurrentIndex((prev) => Math.max(0, prev - step));
  };

  const handleNext = () => {
    const step = isMobile ? 1 : 3;
    const itemsToShow = isMobile ? 1 : 3;
    const maxIdx = Math.max(0, filteredProjects.length - itemsToShow);
    setCurrentIndex((prev) => Math.min(maxIdx, prev + step));
  };

  // Desktop shows 3, mobile shows 1
  const itemsToShow = isMobile ? 1 : 3;
  const visibleProjects = filteredProjects.slice(currentIndex, currentIndex + itemsToShow);
  const maxIndex = Math.max(0, filteredProjects.length - itemsToShow);
  const totalPages = Math.ceil(filteredProjects.length / itemsToShow);
  const currentPage = Math.floor(currentIndex / itemsToShow);

  return (
    <section className="py-20 bg-elate-black" data-testid="featured-work-section">
      <div className="container-custom">
        {/* Header with Tabs */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8">
            <div>
              <p className="caption text-elate-yellow mb-4">Selected Work</p>
              <h2 className="text-subhead text-white">
                Brands We've <span className="text-elate-yellow">Empowered</span>
              </h2>
            </div>
            <Link
              to="/work"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-elate-yellow uppercase tracking-wider text-sm font-semibold hover:gap-4 transition-all"
            >
              View All Work
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-5 py-2 uppercase tracking-wider text-xs font-semibold transition-all ${
                  activeFilter === filter
                    ? 'bg-elate-yellow text-elate-black'
                    : 'bg-transparent text-gray-400 hover:text-white border border-white/20 hover:border-white/40'
                }`}
                data-testid={`featured-filter-${filter}`}
              >
                {filter === 'all' ? 'All' : `#${filter}`}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Carousel - NO AUTOPLAY */}
        <div className="relative">
          {/* Navigation Arrows - hidden on mobile, use swipe or dots instead */}
          <div className="hidden md:block">
            {filteredProjects.length > 3 && (
              <>
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-12 h-12 bg-elate-yellow text-elate-black flex items-center justify-center transition-opacity ${
                    currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white'
                  }`}
                  aria-label="Previous"
                >
                  <ArrowLeft size={20} />
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentIndex >= maxIndex}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-12 h-12 bg-elate-yellow text-elate-black flex items-center justify-center transition-opacity ${
                    currentIndex >= maxIndex ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white'
                  }`}
                  aria-label="Next"
                >
                  <ArrowRight size={20} />
                </button>
              </>
            )}
          </div>

          {/* Cards - 1 on mobile, 3 on desktop */}
          <div className="overflow-hidden">
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
              key={`${activeFilter}-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {visibleProjects.map((project, index) => (
                  <motion.div
                    key={`${project.title}-${currentIndex}-${index}`}
                    className="group relative aspect-[4/5] overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <img
                      src={project.image}
                      alt={project.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-elate-black via-elate-black/40 to-transparent" />
                    
                    {/* Custom colour gradient overlay for specific clients */}
                    {project.gradient && (
                      <div className={`absolute inset-0 ${project.gradientDirection} ${project.gradient}`} />
                    )}
                    
                    {/* Colour logo overlay for clients with logos */}
                    {project.logo && (
                      <div className="absolute top-6 left-6 right-6">
                        <img
                          src={project.logo}
                          alt={`${project.title} logo`}
                          className={`w-auto object-contain drop-shadow-lg ${
                            project.logoSize === 'xlarge' ? 'h-20' : 
                            project.logoSize === 'large' ? 'h-16' : 'h-12'
                          }`}
                        />
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="inline-block px-2 py-1 bg-black/50 text-elate-yellow text-xs uppercase tracking-wider mb-2">#{project.category}</p>
                      <h3 className="text-2xl font-heading uppercase text-white mb-1">
                        {project.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4">{project.subtitle}</p>
                      {project.link ? (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-elate-yellow text-sm uppercase tracking-wider font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          View Project <ArrowUpRight size={14} />
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-gray-500 text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                          Case Study
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
            </motion.div>
          </div>

          {/* Pagination Dots - show for mobile (1 item) or desktop (3+ items) */}
          {filteredProjects.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i * itemsToShow)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentPage === i 
                      ? 'bg-elate-yellow w-6' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// AI Pivot Message Section
const AIPivot = () => {
  return (
    <section className="py-24 bg-elate-yellow" data-testid="ai-pivot-section">
      <div className="container-custom">
        <motion.div
          className="max-w-4xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-elate-black/60 uppercase tracking-widest text-sm mb-6">The Shift</p>
          <h2 className="text-4xl md:text-5xl font-heading uppercase text-elate-black mb-8 leading-tight">
            We Saw the Change Coming
          </h2>
          <p className="text-xl text-elate-black/80 leading-relaxed mb-6">
            For over 15 years, we've been building brands across sport, entertainment, music, and the city. When AI emerged as a transformative force, we pivoted all our efforts into mastering it.
          </p>
          <p className="text-xl text-elate-black/80 leading-relaxed mb-8">
            Now we're not just building websites—we're developing in-house AI systems that future-proof brands and accelerate business growth. Strategy, creative, and automation combined.
          </p>
          <Link
            to="/ai-consulting"
            className="bg-elate-black text-white font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-elate-black transition-colors"
          >
            Explore AI Services
            <ArrowUpRight size={20} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Cpu,
      title: 'AI Consulting',
      description: 'Strategic AI implementation that transforms your business operations and keeps you ahead of the curve.',
      link: '/ai-consulting',
    },
    {
      icon: Zap,
      title: 'AI Creation',
      description: 'Custom AI tools, automations, and intelligent agents built specifically for your business needs.',
      link: '/ai-creation',
    },
    {
      icon: Palette,
      title: 'Media & Brand',
      description: 'Compelling brand strategy, creative campaigns, and content that resonates and performs.',
      link: '/media-brand',
    },
    {
      icon: Target,
      title: 'Sectors',
      description: 'Deep expertise across Sport, City, Music, and Entertainment industries.',
      link: '/sectors',
    },
  ];

  return (
    <section className="section-padding bg-elate-surface" data-testid="services-section">
      <div className="container-custom">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="caption text-elate-yellow mb-4">What We Do</p>
          <h2 className="text-headline text-white">
            Strategy Meets
            <br />
            <span className="text-elate-yellow">Innovation</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={service.link}
                className="card-elate block p-8 md:p-12 h-full group"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-elate-yellow mb-6" />
                <h3 className="text-2xl md:text-3xl font-heading uppercase text-white mb-4 group-hover:text-elate-yellow transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <span className="inline-flex items-center gap-2 text-elate-yellow text-sm uppercase tracking-wider font-semibold group-hover:gap-4 transition-all">
                  Learn More
                  <ArrowRight size={16} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sectors Section - City FIRST, with Brandsmiths logo
const Sectors = () => {
  const sectors = [
    {
      name: 'City',
      image: '/images/clients/Brandsmiths Background - City.jpg',
      logo: '/images/clients/Brandsmiths Logo.png',
      clients: ['Brandsmiths', 'VenCap', 'Hagen Wolf', 'NicheCom', 'KKV'],
      gradient: 'from-[#E91E63]/40 via-transparent to-transparent',
      gradientDirection: 'bg-gradient-to-t',
    },
    {
      name: 'Sport',
      image: '/images/clients/laurence okolie world title.jpg',
      clients: ['Callum Ilott', 'Lawrence Okolie', 'Joe Joyce', 'Johnny Fisher'],
    },
    {
      name: 'Music',
      image: '/images/clients/stilesdrewe.jpg',
      clients: ['Stiles & Drewe', 'Kerry Ellis', 'Mark Kingswood'],
    },
    {
      name: 'Entertainment',
      image: '/images/clients/Teletubbies Entertainment.jpg',
      clients: ['Teletubbies', 'Tom Gates', 'Fat Friends', 'Fireman Sam'],
    },
  ];

  return (
    <section className="section-padding" data-testid="sectors-section">
      <div className="container-custom">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="caption text-elate-yellow mb-4">Industries</p>
          <h2 className="text-headline text-white">
            Sector
            <br />
            <span className="text-elate-yellow">Expertise</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sectors.map((sector, index) => (
            <motion.div
              key={sector.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={`/sectors#${sector.name.toLowerCase()}`}
                className="block relative aspect-[3/4] overflow-hidden group"
                data-testid={`sector-${sector.name.toLowerCase()}`}
              >
                <img
                  src={sector.image}
                  alt={sector.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="sector-overlay" />
                
                {/* Custom colour gradient overlay */}
                {sector.gradient && (
                  <div className={`absolute inset-0 ${sector.gradientDirection} ${sector.gradient}`} />
                )}
                
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-elate-yellow transition-colors duration-300" />
                
                {/* Logo for City sector */}
                {sector.logo && (
                  <div className="absolute top-4 left-4 right-4">
                    <img
                      src={sector.logo}
                      alt=""
                      className="h-10 w-auto object-contain drop-shadow-lg"
                    />
                  </div>
                )}
                
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="caption text-elate-yellow mb-2">#{sector.name}</p>
                  <h3 className="text-3xl font-heading uppercase text-white">
                    {sector.name}
                  </h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Client Logos Section - Added NicheCom
const ClientLogos = () => {
  const logos = [
    { name: 'Brandsmiths', logo: '/images/clients/Brandsmiths-Logo-White.png' },
    { name: 'VenCap', logo: '/images/clients/VenCap Logo.png' },
    { name: 'NicheCom', logo: '/images/clients/Nichecom Logo.png' },
    { name: 'Hagen Wolf', logo: '/images/clients/HAgen Wolf Logo.png' },
    { name: 'KKV', logo: '/images/clients/KKV Logo.png' },
    { name: 'LTS Group', logo: '/images/clients/LTS Group Logo.png' },
  ];

  return (
    <section className="py-16 bg-elate-surface border-y border-white/10" data-testid="client-logos-section">
      <div className="container-custom">
        <motion.p
          className="text-center text-gray-500 uppercase tracking-widest text-sm mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Trusted by leading brands
        </motion.p>
        <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16">
          {logos.map((client, index) => (
            <motion.div
              key={client.name}
              className="opacity-60 hover:opacity-100 transition-opacity"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 0.6, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              whileHover={{ opacity: 1 }}
            >
              <img
                src={client.logo}
                alt={client.name}
                className="h-10 md:h-12 w-auto object-contain filter brightness-0 invert"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section - Updated image to Lawrence Okolie
const Stats = () => {
  const stats = [
    { number: '100+', label: 'Projects Delivered' },
    { number: '15+', label: 'Years Experience' },
    { number: '4', label: 'Specialist Sectors' },
    { number: '2', label: 'AI Trained Staff' },
  ];

  return (
    <section className="py-20 border-y border-white/10 relative overflow-hidden" data-testid="stats-section">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/clients/laurence okolie world title.jpg" 
          alt="" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-elate-black/80" />
      </div>
      
      <div className="container-custom relative z-10">
        <motion.p
          className="text-center text-elate-yellow uppercase tracking-widest text-sm mb-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Brands That Perform
        </motion.p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <p className="text-5xl md:text-6xl font-heading text-elate-yellow mb-2">
                {stat.number}
              </p>
              <p className="text-gray-400 text-sm uppercase tracking-wider">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <Hero />
      <FeaturedWork />
      <AIPivot />
      <Services />
      <Sectors />
      <ClientLogos />
      <Stats />
    </div>
  );
};

export default HomePage;
